import React, { useMemo } from "react";
import { Tooltip } from "antd";
import { useSelector } from "react-redux";
import { getLeadOpportunityById, getRootOppTypeId } from "../library/oppTypeScoping";
import { getRootConfig } from "../library/oppTypeRootConfig";
import {
  ALL_OPPORTUNITY_TYPES_ID,
  formatOpportunityId,
  prepareOpportunityList,
} from "../helpers/opportunityHelper";
import useCrmIntegration from "./useCrmIntegration";

/**
 * Single source of truth for the per-message root opp-type badge
 * (Sales/Service/Parts/...) shown next to TO_LEAD messages. Every
 * message-rendering surface (SMS, MMS, Email, Call, the centered
 * Otto/campaign/review banner) calls this hook instead of computing the
 * badge itself — a future icon/tooltip/gating change only touches this
 * file, never the callers.
 *
 * Resolution: only computed while the user is viewing "All opportunities"
 * (a specific opp/root selection means every visible message already
 * belongs to that scope, so a per-message badge would be redundant) —
 * gated via Redux directly so callers never need to know about it.
 *
 * @param {string|number} leadOpportunityId — scalar id off the message/call
 * @param {Array} leadOpportunities — ``selectedLead.leadOpportunities``
 * @returns {React.ReactNode|null}
 */
export const useOpportunityBadge = (leadOpportunityId, leadOpportunities) => {
  const { messageOpportunity } = useSelector(state => state.lead);
  const isAllOpportunitySelected = messageOpportunity === ALL_OPPORTUNITY_TYPES_ID;
  // Same CRM-id resolution TopToolbarJsx uses (extractOpportunityId via
  // prepareOpportunityList) — reusing it directly (rather than the BE's
  // separate ``crmLink`` field) guarantees this badge's tooltip can never
  // show a different id/prefix than the opp dropdown for the same opp.
  const crmIntegrationType = useCrmIntegration();

  return useMemo(() => {
    if (!isAllOpportunitySelected) return null;
    const opp = getLeadOpportunityById(leadOpportunityId, leadOpportunities);
    const rootId = getRootOppTypeId(opp?.opportunityType);
    if (!rootId) return null;
    const cfg = getRootConfig(rootId);
    const { Icon } = cfg;
    const [prepared] = prepareOpportunityList([opp], crmIntegrationType);
    // "DS 1738027" (or "EAI 0042" for opps without a real CRM id) — same
    // padding + format convention as TopToolbarJsx's getFormattedEventId.
    const formattedOppId = formatOpportunityId(
      prepared?.prefix || "EAI",
      String(prepared?.eventId ?? opp?.id).padStart(4, "0")
    );
    // "Sales opportunity: Toyota Camry Interest (DS 1738027)" — falls
    // back to just the formatted id when the opp has no name.
    const oppLabel = opp?.name ? `${opp.name} (${formattedOppId})` : formattedOppId;
    return (
      <Tooltip title={`${cfg.displayName} opportunity: ${oppLabel}`}>
        <span className="inline-flex items-center">
          <Icon size={14} color={cfg.stroke} strokeWidth={1.66} />
        </span>
      </Tooltip>
    );
  }, [isAllOpportunitySelected, leadOpportunityId, leadOpportunities, crmIntegrationType]);
};

export default useOpportunityBadge;
